import { wallOpeningOpening3 } from "building-common/openings/openingQuery";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { DocEntityHandle, SiteHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { ExteriorMaterialOverride } from "building-types/materials/materialTypes";
import type { SiteGeomTagGroup } from "building-types/site/siteGeomTagGroup";
import type { Table } from "common/data/table";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { exteriorMaterialOverrideNew } from "doc/utils/materialTools";
import type { BRep } from "geom/brep";
import { faceByFourPoints, faceByThreePoints } from "geom/brep";
import { closestPointBRep } from "geom/closestPoint";
import { ZERO_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { extrudeFaceAlongPolyLine3 } from "geom/extrude";
import GeomTagGroup from "geom/geomTagGroup";
import { planarGraphHalfEdgeEval, planarGraphHalfEdgeNormal } from "geom/planarGraph";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polyLine2Move, polyLine2SetCW } from "geom/polyLine2";
import { polyLine3ExtendEnd, polyLine3ExtendStart } from "geom/polyLine3";
import type { PolyLine3 } from "geom/polyLine3";
import { rect2ByTwoPoints } from "geom/rect2";
import { rtreeBRepFaces } from "geom/rtree";
import { vec2Dot, vec2Perp } from "geom/vec2";
import type { RBush3D } from "rbush-3d";
import { isGarageDoorOperationType } from "../doors/doorUtils";
import { groundLevelId } from "../level/levelTools";

export const DRIVEWAY_MERGE_THRESHOLD = 100;
export const SITE_THICKNESS = 500;

interface DoorData {
  readonly doorElevation: number;
  readonly type?: SiteDoorType;
  readonly drivewayInfo: {
    normalX: number;
    normalY: number;
    startX: number;
    startY: number;
    endX: number;
    endY: number;
  }[];
}

export enum SiteDoorType {
  GARAGE = "GARAGE",
  ENTRY = "ENTRY",
}

export function getSiteDoorData(
  levels: Table<Level>,
  planarGraphs: Table<PolygonPlanarGraph>,
  doors: Table<GeneratedDoorPlacement>,
  type: SiteDoorType,
): DoorData | undefined {
  // get the garage elevation
  // todo, publish this from the Driveway step?
  const groundLevelIndex = groundLevelId(levels);
  if (!groundLevelIndex) {
    return undefined;
  }

  const planarGraph = planarGraphs[groundLevelIndex].planarGraph;
  const garageLevel = levels[groundLevelIndex];

  let doorElevation = 0;

  if (type === SiteDoorType.ENTRY) {
    const entryDoorInfo: {
      normalX: number;
      normalY: number;
      startX: number;
      startY: number;
      endX: number;
      endY: number;
    }[] = [];

    //Figure out which doors need sidewalks.
    const entryDoors: { [halfEdgeIndex: number]: GeneratedDoorPlacement[] } = {};
    for (const doorId in doors) {
      const door = doors[doorId];
      if (!door.frontRoom) {
        continue;
      }
      if (door.frontRoom.level.id !== groundLevelIndex) {
        continue;
      }
      if (door.descriptor.isPrimaryDoor && door.opening) {
        const opening3 = wallOpeningOpening3(door.opening, "FRONT", false);
        assertIsNotUndefined(opening3);

        doorElevation = opening3[2];
        // const includeSidewalk = door.includeDriveway ?? door.descriptor.includeDriveway;
        // if (!includeDriveway) {
        //   continue;
        // }
        if (entryDoors[door.halfEdgeIndex] === undefined) {
          entryDoors[door.halfEdgeIndex] = [door];
        } else {
          entryDoors[door.halfEdgeIndex].push(door);
        }
      }
    }

    for (const halfEdgeIndex in entryDoors) {
      const doors = entryDoors[halfEdgeIndex];
      if (doors.length === 0) {
        continue;
      }
      doors.sort((a, b) => {
        return (a.opening?.back.startParam ?? 0) - (b.opening?.back.startParam ?? 0);
      });
      const hei = Number(halfEdgeIndex);
      const [startX, startY] = planarGraphHalfEdgeEval(planarGraph, hei, doors[0].opening?.back.startParam ?? 0);
      const [endX, endY] = planarGraphHalfEdgeEval(
        planarGraph,
        hei,
        doors[doors.length - 1].opening?.back.endParam ?? 0,
      );
      const [normalX, normalY] = planarGraphHalfEdgeNormal(planarGraph, hei);
      entryDoorInfo.push({ startX, startY, endX, endY, normalX, normalY });
    }

    return {
      drivewayInfo: entryDoorInfo,
      doorElevation,
    };
  }

  if (type === SiteDoorType.GARAGE) {
    doorElevation = garageLevel.floorElevation + garageLevel.levelDatumsGenerated.grade;
    // Consolidate doors that share the same wall into the same driveway
    const garageDoorInfo: {
      normalX: number;
      normalY: number;
      startX: number;
      startY: number;
      endX: number;
      endY: number;
    }[] = [];

    // Figure out which doors need driveways.
    const garageDoors: { [halfEdgeIndex: number]: GeneratedDoorPlacement[] } = {};
    for (const doorId in doors) {
      const door = doors[doorId];
      if (!door.frontRoom) {
        continue;
      }
      if (door.frontRoom.level.id !== groundLevelIndex) {
        continue;
      }
      if (isGarageDoorOperationType(door) && door.opening) {
        const includeDriveway = door.includeDriveway ?? door.descriptor.includeDriveway;
        if (!includeDriveway) {
          continue;
        }

        if (garageDoors[door.halfEdgeIndex] === undefined) {
          garageDoors[door.halfEdgeIndex] = [door];
        } else {
          garageDoors[door.halfEdgeIndex].push(door);
        }
      }
    }

    for (const halfEdgeIndex in garageDoors) {
      const doors = garageDoors[halfEdgeIndex];
      if (doors.length === 0) {
        continue;
      }
      doors.sort((a, b) => {
        return (a.opening?.back.startParam ?? 0) - (b.opening?.back.startParam ?? 0);
      });
      const hei = Number(halfEdgeIndex);
      const [startX, startY] = planarGraphHalfEdgeEval(planarGraph, hei, doors[0].opening?.back.startParam ?? 0);
      const [endX, endY] = planarGraphHalfEdgeEval(
        planarGraph,
        hei,
        doors[doors.length - 1].opening?.back.endParam ?? 0,
      );
      const [normalX, normalY] = planarGraphHalfEdgeNormal(planarGraph, hei);
      garageDoorInfo.push({ startX, startY, endX, endY, normalX, normalY });
    }

    // Adjust driveways so they join if they are close together.
    garageDoorInfo.sort((a, b) => {
      // Sort by direction.
      const aDir = vec2Dot(a.normalX, a.normalY, 0, -1);
      const bDir = vec2Dot(b.normalX, b.normalY, 0, -1);
      if (aDir - bDir < ZERO_EPSILON) {
        // Sort by furthest from house in normal direction.
        const aDist = vec2Dot(a.startX, a.startY, a.normalX, a.normalY);
        const bDist = vec2Dot(b.startX, b.startY, a.normalX, a.normalY);
        return bDist - aDist; // so we get greater first
      } else {
        return aDir - bDir;
      }
    });

    // Try to expand driveways that are closer to the street to meet the ones that
    // started closer to the house.
    for (let i = 0, ic = garageDoorInfo.length; i < ic; i++) {
      const toAdjust = garageDoorInfo[i];
      const [searchX, searchY] = vec2Perp(toAdjust.normalX, toAdjust.normalY);
      const toAdjustStart = vec2Dot(toAdjust.startX, toAdjust.startY, searchX, searchY);
      const toAdjustEnd = vec2Dot(toAdjust.endX, toAdjust.endY, searchX, searchY);

      let sameNormal = true;
      for (let j = i + 1; j < ic; j++ && sameNormal) {
        // Make sure they are pointed the same way.
        const toMatch = garageDoorInfo[j];
        sameNormal =
          vec2Dot(toAdjust.normalX, toAdjust.normalY, toMatch.normalX, toMatch.normalY) >= 1.0 - DIST_EPSILON_SQUARED;
        if (sameNormal) {
          // connectedDriveway = true;
          const toMatchStart = vec2Dot(toMatch.startX, toMatch.startY, searchX, searchY);
          const toMatchEnd = vec2Dot(toMatch.endX, toMatch.endY, searchX, searchY);
          const startAdjust = toMatchEnd - toAdjustStart;
          if (startAdjust < DRIVEWAY_MERGE_THRESHOLD && startAdjust > 0) {
            toAdjust.startX += startAdjust * searchX;
            toAdjust.startY += startAdjust * searchY;
          } else {
            const endAdjust = toAdjustEnd - toMatchStart;
            if (endAdjust < DRIVEWAY_MERGE_THRESHOLD && endAdjust > 0) {
              toAdjust.endX -= endAdjust * searchX;
              toAdjust.endY -= endAdjust * searchY;
            }
          }
        }
      }
    }

    return {
      drivewayInfo: garageDoorInfo,
      doorElevation,
    };
  }

  return undefined;
}

export interface SiteZGeom {
  geometry: BRep;
  rtreeCached?: RBush3D;
}

export function getSiteZ(siteGeom: SiteZGeom, x: number, y: number): number {
  if (!siteGeom.rtreeCached) {
    siteGeom.rtreeCached = rtreeBRepFaces(siteGeom.geometry);
  }
  const z = closestPointBRep(siteGeom.geometry, x, y, 0, 500, siteGeom.rtreeCached)?.z;
  return z ?? 0;
}

interface MakeSidewalkOptions {
  sidewalkRail1: PolyLine3;
  sidewalkRail2: PolyLine3;
  sidewalkRailFull: PolyLine3;
  driveStartX: number;
  driveEndX: number;
  frontBoundary: number;
  grassCurbWidth: number;
  sidewalkWidth: number;
  curbHeight: number;
  garageElevation: number;
  roadVerticalOffset: number;
  sidewalkRampLength: number;
  roadWidth: number;
}

interface SidewalkGeometry {
  sidewalkGeom: SiteGeomTagGroup[];
  grassGeom: SiteGeomTagGroup[];
}

export function makeSidewalk(options: MakeSidewalkOptions): SidewalkGeometry {
  const {
    sidewalkRail1,
    sidewalkRail2,
    sidewalkRailFull,
    driveStartX,
    driveEndX,
    frontBoundary,
    grassCurbWidth,
    sidewalkWidth,
    curbHeight,
    garageElevation,
    roadVerticalOffset,
    sidewalkRampLength,
    roadWidth,
  } = options;

  const ret: SidewalkGeometry = {
    sidewalkGeom: [],
    grassGeom: [],
  };

  const sidewalkProfile = rect2ByTwoPoints(0, 0, -sidewalkWidth, curbHeight);
  polyLine2SetCW(sidewalkProfile);
  polyLine2Move(sidewalkProfile, -grassCurbWidth, 0);

  const grassCurbProfile = rect2ByTwoPoints(0, 0, -grassCurbWidth, curbHeight - 1);
  polyLine2SetCW(grassCurbProfile);

  const sidewalk1 = extrudeFaceAlongPolyLine3([sidewalkProfile], sidewalkRail1, 0, 0, 1);
  const sidewalk2 = extrudeFaceAlongPolyLine3([sidewalkProfile], sidewalkRail2, 0, 0, 1);

  // Create ramp faces
  const sidewalkLeftRampFace = faceByFourPoints(
    driveStartX - sidewalkRampLength,
    frontBoundary + grassCurbWidth + sidewalkWidth,
    garageElevation + curbHeight + roadVerticalOffset,
    driveStartX - sidewalkRampLength,
    frontBoundary + grassCurbWidth,
    garageElevation + roadVerticalOffset + curbHeight,
    driveStartX,
    frontBoundary + grassCurbWidth,
    garageElevation + roadVerticalOffset,
    driveStartX,
    frontBoundary + grassCurbWidth + sidewalkWidth,
    garageElevation + roadVerticalOffset,
  );

  const sidewalkRightRampFace = faceByFourPoints(
    driveEndX,
    frontBoundary + grassCurbWidth + sidewalkWidth,
    garageElevation + roadVerticalOffset,
    driveEndX,
    frontBoundary + grassCurbWidth,
    garageElevation + roadVerticalOffset,
    driveEndX + sidewalkRampLength,
    frontBoundary + grassCurbWidth,
    garageElevation + roadVerticalOffset + curbHeight,
    driveEndX + sidewalkRampLength,
    frontBoundary + grassCurbWidth + sidewalkWidth,
    garageElevation + curbHeight + roadVerticalOffset,
  );

  const sidewalkLeftRampFront = faceByThreePoints(
    driveStartX - sidewalkRampLength,
    frontBoundary + grassCurbWidth,
    garageElevation + curbHeight + roadVerticalOffset,
    driveStartX - sidewalkRampLength,
    frontBoundary + grassCurbWidth,
    garageElevation + roadVerticalOffset,
    driveStartX,
    frontBoundary + grassCurbWidth,
    garageElevation + roadVerticalOffset,
  );

  const sidewalkRightRampFront = faceByThreePoints(
    driveEndX,
    frontBoundary + grassCurbWidth,
    garageElevation + roadVerticalOffset,
    driveEndX + sidewalkRampLength,
    frontBoundary + grassCurbWidth,
    garageElevation + roadVerticalOffset,
    driveEndX + sidewalkRampLength,
    frontBoundary + grassCurbWidth,
    garageElevation + curbHeight + roadVerticalOffset,
  );

  [sidewalkLeftRampFace, sidewalkRightRampFace, sidewalkLeftRampFront, sidewalkRightRampFront].forEach((face) => {
    if (face) {
      ret.sidewalkGeom.push({
        group: new GeomTagGroup<ExteriorMaterialOverride>().add(face, exteriorMaterialOverrideNew("driveway0")),
        pickHandle: undefined,
      });
    }
  });

  ret.sidewalkGeom.push({
    group: new GeomTagGroup<ExteriorMaterialOverride>().add(sidewalk1, exteriorMaterialOverrideNew("sidewalk0")),
    pickHandle: undefined,
  });
  ret.sidewalkGeom.push({
    group: new GeomTagGroup<ExteriorMaterialOverride>().add(sidewalk2, exteriorMaterialOverrideNew("sidewalk0")),
    pickHandle: undefined,
  });

  // Opposite side of the street
  const oppSidewalk = polyLine2Move(sidewalkProfile, roadWidth + grassCurbWidth + sidewalkWidth + 18, 0);
  const oppSidewalkGeom = extrudeFaceAlongPolyLine3([oppSidewalk], sidewalkRailFull, 0, 0, 1);
  ret.sidewalkGeom.push({
    group: new GeomTagGroup<ExteriorMaterialOverride>().add(oppSidewalkGeom, exteriorMaterialOverrideNew("sidewalk0")),
    pickHandle: undefined,
  });

  if (grassCurbWidth > 0) {
    const grassRail1 = polyLine3ExtendEnd(sidewalkRail1, sidewalkRampLength);
    const grassRail2 = polyLine3ExtendStart(sidewalkRail2, sidewalkRampLength);
    const grass1 = extrudeFaceAlongPolyLine3([grassCurbProfile], grassRail1, 0, 0, 1);
    const grass2 = extrudeFaceAlongPolyLine3([grassCurbProfile], grassRail2, 0, 0, 1);

    ret.grassGeom.push({
      group: new GeomTagGroup<ExteriorMaterialOverride>().add(grass1, exteriorMaterialOverrideNew("grass0")),
      pickHandle: undefined,
    });
    ret.grassGeom.push({
      group: new GeomTagGroup<ExteriorMaterialOverride>().add(grass2, exteriorMaterialOverrideNew("grass0")),
      pickHandle: undefined,
    });

    // Opposite side of the street filler
    const oppGrassProfile = rect2ByTwoPoints(roadWidth, 0, 2400, curbHeight - 1);
    polyLine2SetCW(oppGrassProfile);
    const oppGrass = extrudeFaceAlongPolyLine3([oppGrassProfile], sidewalkRailFull, 0, 0, 1);
    ret.grassGeom.push({
      group: new GeomTagGroup<ExteriorMaterialOverride>().add(oppGrass, exteriorMaterialOverrideNew("grass0")),
      pickHandle: undefined,
    });
  }

  return ret;
}

/**
 * Calculates the height relative to the site elevation reference
 * @param absoluteHeight The absolute height value (e.g., 2566')
 * @param siteElevationReference The reference elevation (e.g., 2560')
 * @returns The relative height (e.g., 6')
 */
export function calculateRelativeHeight(absoluteHeight: number, siteElevationReference: number): number {
  // If either value is undefined or not a number, return the absolute height
  if (isNaN(absoluteHeight) || isNaN(siteElevationReference)) {
    return absoluteHeight;
  }
  return absoluteHeight - siteElevationReference;
}

/** The entity a click on a site shape's geometry selects. */
export function siteShapePickHandle(shape: { readonly id: string }, siteId: string): DocEntityHandle {
  const siteHandle: SiteHandle = { type: "SITE", id: siteId };
  return { type: "SITE_SHAPE", id: shape.id, parent: siteHandle };
}
